import React from "react";
import {
  UpdatePasswordPageProps,
  UpdatePasswordFormTypes,
  useActiveAuthProvider,
  useTranslate,
  useUpdatePassword,
  ErrorComponent,
} from "@refinedev/core";
import { ThemedTitleV2 } from "@refinedev/antd";
import {
  Row,
  Col,
  Layout,
  Card,
  Typography,
  Form,
  Input,
  Button,
  LayoutProps,
  CardProps,
  FormProps,
  theme,
} from "antd";
import { PageTitle } from "../login/components/PageTitle";
import { bodyStyles, containerStyles, headStyles, layoutStyles } from "../login/styles";
import { ConfiormPasswordField, PasswordField } from "../register/components/passwords";
import { ResetPasswordFormTypes } from "../../authProvider";
import { useSearchParams } from "react-router-dom";

type UpdatePasswordProps = UpdatePasswordPageProps<
  LayoutProps,
  CardProps,
  FormProps
>;

/**
 * **refine** has update password page form which is served on `/update-password` route when the `authProvider` configuration is provided.
 *
 * @see {@link https://refine.dev/docs/ui-frameworks/antd/components/antd-auth-page/#update-password} for more details.
 */
export const ResetPasswordPage: React.FC<UpdatePasswordProps> = ({
  wrapperProps,
  contentProps,
  renderContent,
  formProps,
  title,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm<ResetPasswordFormTypes>();
  const translate = useTranslate();
  const authProvider = useActiveAuthProvider();
  const [searchParams, setSearchParams] = useSearchParams();
  const hash = searchParams.get('hash');
  if (!hash) {
    return <ErrorComponent />;
  }
  const { mutate: updatePassword, isLoading } =
    useUpdatePassword<UpdatePasswordFormTypes>({
      v3LegacyAuthProviderCompatible: Boolean(authProvider?.isLegacy),
    });

  const CardTitle = (
    <Typography.Title
      level={3}
      style={{
        color: token.colorTextHeading,
      }}
      className="text-center"
    >
      {translate("pages.resetPassword.title")}
    </Typography.Title>
  );

  const CardContent = (
    <Card
      title={CardTitle}
      headStyle={headStyles}
      bodyStyle={bodyStyles}
      style={{
        ...containerStyles,
        backgroundColor: token.colorBgContainer,
        borderColor: token.colorBorder,
      }}
      {...(contentProps ?? {})}
    >
      <Form<ResetPasswordFormTypes>
        layout="vertical"
        form={form}
        onFinish={(values) => updatePassword({ ...values, hash })}
        requiredMark={false}
        {...formProps}
      >
        <PasswordField
          name="password"
          label={translate("pages.resetPassword.fields.password", "New Password")}
          rules={[{ required: true }]}
        />
        <ConfiormPasswordField
          name="confirmPassword"
          label={translate("pages.resetPassword.fields.confirmPassword", "Confirm New Password")}
          dependencies={["password"]}
          rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(translate("pages.resetPassword.errors.confirmPasswordNotMatch", "Passwords do not match"))
                );
              },
            }),
          ]}
        />
        <Form.Item
          style={{
            marginTop: "24px",
            marginBottom: 0,
          }}
        >
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={isLoading}
            block
          >
            {translate("pages.resetPassword.buttons.submit", "Update Password")}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );

  return (
    <Layout style={{ ...layoutStyles, backgroundColor: token.colorBgLayout }} {...(wrapperProps ?? {})}>
      <Row
        justify="center"
        align="middle"
        style={{
          padding: "16px 0",
          minHeight: "100dvh",
          backgroundColor: token.colorBgLayout,
        }}
      >
        <Col xs={22}>
          {renderContent ? (
            renderContent(CardContent, PageTitle)
          ) : (
            <>
              {PageTitle}
              {CardContent}
            </>
          )}
        </Col>
      </Row>
    </Layout>
  );
};
